import {FC, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFlags} from "launchdarkly-react-client-sdk";
import {Box} from "@interstate/components/Box";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon
} from "@interstate/components/Icons";
import {Button} from "@interstate/components/Button";
import {Skeleton} from "@interstate/components/Skeleton";
import {EventHandler, IconType} from "../lib/interstate";
import {Permission, useAuthorization} from "../access";
import {useStickyState} from "../hooks";
import "./Sidebar.scss";

export interface SidebarItem {
  id: string;
  active?: boolean;
  featureFlag?: string;
  permissions?: Permission[];
  labelKey: string;
  icon: IconType;
  onClick: EventHandler;
}

export interface SidebarProps {
  expanded?: boolean;
  items: SidebarItem[];
}

function isItemDefined(item?: SidebarItem): item is SidebarItem {
  return item !== undefined;
}

export const Sidebar: FC<SidebarProps> = ({expanded = false, items}) => {
  const [authorizedItems, setAuthorizedItems] = useState<SidebarItem[]>(() => {
    return [
      {
        id: "skeleton",
        icon: EllipsisHorizontalIcon,
        labelKey: "navigation.skeleton",
        active: false
      } as SidebarItem
    ];
  });
  const flags = useFlags();
  const granted = useAuthorization();
  const {t} = useTranslation();
  const [collapsed, setCollapsed] = useStickyState<boolean>(
    !expanded,
    "coat2.sidebar.collapsed"
  );

  const classes = collapsed
    ? "coat-sidebar--collapsed"
    : "coat-sidebar--expanded";

  useEffect(() => {
    Promise.all(
      items.map(item =>
        item.permissions
          ? granted(item.permissions).then(authorized =>
              authorized ? item : undefined
            )
          : Promise.resolve(item)
      )
    )
      .then(items => items.filter(isItemDefined))
      .then(setAuthorizedItems);
  }, [items, granted]);

  return (
    <Box
      component={"nav"}
      id={"coat-sidebar"}
      data-testid={"coat-sidebar"}
      className={classes}>
      <Box component={"ul"} className={"coat-sidebar-items"}>
        {authorizedItems
          .filter(item => {
            return item.featureFlag ? flags[item.featureFlag] : true;
          })
          .map(item => {
            const itemClasses = `coat-sidebar-item ${
              item.active
                ? "coat-sidebar-item--active"
                : "coat-sidebar-item--inactive"
            }`;
            const linkId = `coat-sidebar-item-link-${item.id}`;
            const Icon = item.icon;
            const label = t(item.labelKey, "???");
            const skeleton = item.id === "skeleton";
            return (
              <li key={item.id} className={itemClasses} onClick={item.onClick}>
                <Button
                  id={linkId}
                  data-testid={linkId}
                  className={"coat-sidebar-item-link"}
                  buttonStyle={"tertiary"}
                  block={true}
                  startIcon={<Icon title={label} />}>
                  {!collapsed && skeleton && (
                    <Skeleton
                      data-testid={`coat-sidebar-item-label-${item.id}`}
                      variant={{type: "text"}}
                      sx={theme => ({
                        width: 103.2
                      })}
                    />
                  )}
                  {!collapsed && !skeleton && (
                    <span className={"coat-sidebar-item-label"}>{label}</span>
                  )}
                </Button>
              </li>
            );
          })}
      </Box>
      <Button
        id={"coat-sidebar-control"}
        data-testid={"coat-sidebar-control"}
        size={"small"}
        buttonStyle={"secondary"}
        startIcon={
          collapsed ? (
            <ChevronRightIcon
              viewBox={"-3 -2 20 20"}
              title={t("navigation.expand", "???") || "???"}
            />
          ) : (
            <ChevronLeftIcon
              viewBox={"-1 -2 20 20"}
              title={t("navigation.collapse", "???") || "???"}
            />
          )
        }
        onClick={() => setCollapsed(value => !value)}
      />
    </Box>
  );
};
